import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { trackDayPassTrayOpen } from "@/utils/analytics";

// Day Pass base price per person
export const DAY_PASS_BASE_PRICE = 200;
// Included 15-minute massage value (to subtract from upgrade price)
export const INCLUDED_MASSAGE_VALUE = 40;

export interface MassageUpgrade {
    selected: boolean;
    treatmentId: string | null;
    treatmentName: string | null;
    treatmentPrice: number;
    upgradePrice: number; // treatmentPrice - INCLUDED_MASSAGE_VALUE
}

export interface GuestMassageUpgrade {
    guestIndex: number; // 0-based index
    upgrade: MassageUpgrade;
}

export interface DayPassFormData {
    fullName: string;
    email: string;
    phone: string;
    preferredDate: string; // YYYY-MM-DD format
    preferredTime: "Morning" | "Afternoon" | "Evening";
    specialRequests: string;
}

interface DayPassContextType {
    // Guest count (1-12)
    numberOfGuests: number;
    setNumberOfGuests: (count: number) => void;
    incrementGuests: () => void;
    decrementGuests: () => void;

    // Per-guest massage upgrades (array of upgrades, one per guest)
    guestMassageUpgrades: GuestMassageUpgrade[];
    setGuestMassageUpgrade: (guestIndex: number, upgrade: MassageUpgrade) => void;
    clearGuestMassageUpgrade: (guestIndex: number) => void;
    clearAllMassageUpgrades: () => void;
    getGuestMassageUpgrade: (guestIndex: number) => MassageUpgrade;

    // Form data
    formData: DayPassFormData;
    setFormData: (data: Partial<DayPassFormData>) => void;
    resetFormData: () => void;

    // Price calculations
    basePassPrice: number; // DAY_PASS_BASE_PRICE * numberOfGuests
    upgradePrice: number; // Sum of all individual guest upgrade prices
    totalPrice: number; // basePassPrice + upgradePrice

    // Tray state
    isTrayOpen: boolean;
    traySource: string | null; // Source where tray was opened from
    setIsTrayOpen: (open: boolean, source?: string, activityId?: string, activityName?: string) => void;

    // Reset all state
    resetAll: () => void;
}

const DayPassContext = createContext<DayPassContextType | undefined>(undefined);

const LOCAL_STORAGE_KEY = "beatenberg_day_pass_booking";

const defaultFormData: DayPassFormData = {
    fullName: "",
    email: "",
    phone: "",
    preferredDate: "",
    preferredTime: "Morning",
    specialRequests: "",
};

const defaultMassageUpgrade: MassageUpgrade = {
    selected: false,
    treatmentId: null,
    treatmentName: null,
    treatmentPrice: 0,
    upgradePrice: 0,
};

const createDefaultGuestUpgrades = (count: number): GuestMassageUpgrade[] => {
    return Array.from({ length: count }, (_, index) => ({
        guestIndex: index,
        upgrade: { ...defaultMassageUpgrade },
    }));
};

export const DayPassProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [numberOfGuests, setNumberOfGuestsState] = useState(1);
    const [guestMassageUpgrades, setGuestMassageUpgradesState] = useState<GuestMassageUpgrade[]>(() => 
        createDefaultGuestUpgrades(1)
    );
    const [formData, setFormDataState] = useState<DayPassFormData>(defaultFormData);
    const [isTrayOpen, setIsTrayOpenState] = useState(false);
    const [traySource, setTraySource] = useState<string | null>(null);
    const [isHydrated, setIsHydrated] = useState(false);

    // Wrapper for setIsTrayOpen with tracking
    const setIsTrayOpen = useCallback((open: boolean, source?: string, activityId?: string, activityName?: string) => {
        if (open) {
            const normalizedSource = source || 'unknown';
            trackDayPassTrayOpen(
                normalizedSource,
                activityId,
                activityName
            );
            setTraySource(normalizedSource); // Store source for close event
        } else {
            setTraySource(null); // Clear source when tray closes
        }
        setIsTrayOpenState(open);
    }, []);

    // Load from local storage on mount
    useEffect(() => {
        if (typeof localStorage === 'undefined') return; // SSR safety
        const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (parsed.numberOfGuests) {
                    const guestCount = parsed.numberOfGuests;
                    setNumberOfGuestsState(guestCount);
                    // Migrate old single upgrade format to new per-guest format if needed
                    if (parsed.massageUpgrade && parsed.massageUpgrade.selected) {
                        // Old format: apply same upgrade to all guests
                        const upgrades = createDefaultGuestUpgrades(guestCount);
                        upgrades.forEach(guest => {
                            guest.upgrade = { ...parsed.massageUpgrade };
                        });
                        setGuestMassageUpgradesState(upgrades);
                    } else if (parsed.guestMassageUpgrades) {
                        // New format: restore per-guest upgrades
                        setGuestMassageUpgradesState(parsed.guestMassageUpgrades);
                    } else {
                        setGuestMassageUpgradesState(createDefaultGuestUpgrades(guestCount));
                    }
                }
                if (parsed.formData) setFormDataState(parsed.formData);
            } catch (e) {
                console.error("Failed to parse saved day pass booking", e);
            }
        }
        setIsHydrated(true);
    }, []);

    // Save to local storage on change
    useEffect(() => {
        if (isHydrated) {
            localStorage.setItem(
                LOCAL_STORAGE_KEY,
                JSON.stringify({
                    numberOfGuests,
                    guestMassageUpgrades,
                    formData,
                })
            );
        }
    }, [numberOfGuests, guestMassageUpgrades, formData, isHydrated]);

    const setNumberOfGuests = useCallback((count: number) => {
        const clamped = Math.max(1, Math.min(12, count));
        setNumberOfGuestsState((prev) => {
            const newCount = clamped;
            // Adjust guest upgrades array when guest count changes
            setGuestMassageUpgradesState((prevUpgrades) => {
                if (newCount > prev) {
                    // Adding guests: add default upgrades for new guests
                    return [
                        ...prevUpgrades,
                        ...createDefaultGuestUpgrades(newCount - prev).map(g => ({
                            ...g,
                            guestIndex: prevUpgrades.length + g.guestIndex,
                        })),
                    ];
                } else {
                    // Removing guests: keep only upgrades for remaining guests
                    return prevUpgrades.slice(0, newCount);
                }
            });
            return newCount;
        });
    }, []);

    const incrementGuests = useCallback(() => {
        setNumberOfGuestsState((prev) => {
            const newCount = Math.min(12, prev + 1);
            if (newCount > prev) {
                setGuestMassageUpgradesState((prevUpgrades) => [
                    ...prevUpgrades,
                    {
                        guestIndex: prev,
                        upgrade: { ...defaultMassageUpgrade },
                    },
                ]);
            }
            return newCount;
        });
    }, []);

    const decrementGuests = useCallback(() => {
        setNumberOfGuestsState((prev) => {
            const newCount = Math.max(1, prev - 1);
            if (newCount < prev) {
                setGuestMassageUpgradesState((prevUpgrades) => prevUpgrades.slice(0, newCount));
            }
            return newCount;
        });
    }, []);

    const setGuestMassageUpgrade = useCallback((guestIndex: number, upgrade: MassageUpgrade) => {
        setGuestMassageUpgradesState((prev) => {
            const updated = [...prev];
            if (updated[guestIndex]) {
                updated[guestIndex] = {
                    ...updated[guestIndex],
                    upgrade,
                };
            }
            return updated;
        });
    }, []);

    const clearGuestMassageUpgrade = useCallback((guestIndex: number) => {
        setGuestMassageUpgradesState((prev) => {
            const updated = [...prev];
            if (updated[guestIndex]) {
                updated[guestIndex] = {
                    ...updated[guestIndex],
                    upgrade: { ...defaultMassageUpgrade },
                };
            }
            return updated;
        });
    }, []);

    const clearAllMassageUpgrades = useCallback(() => {
        setGuestMassageUpgradesState((prev) =>
            prev.map((guest) => ({
                ...guest,
                upgrade: { ...defaultMassageUpgrade },
            }))
        );
    }, []);

    const getGuestMassageUpgrade = useCallback((guestIndex: number): MassageUpgrade => {
        return guestMassageUpgrades[guestIndex]?.upgrade || { ...defaultMassageUpgrade };
    }, [guestMassageUpgrades]);

    const setFormData = useCallback((data: Partial<DayPassFormData>) => {
        setFormDataState((prev) => ({ ...prev, ...data }));
    }, []);

    const resetFormData = useCallback(() => {
        setFormDataState(defaultFormData);
    }, []);

    const resetAll = useCallback(() => {
        setNumberOfGuestsState(1);
        setGuestMassageUpgradesState(createDefaultGuestUpgrades(1));
        setFormDataState(defaultFormData);
    }, []);

    // Calculate prices
    const basePassPrice = DAY_PASS_BASE_PRICE * numberOfGuests;
    const upgradePrice = guestMassageUpgrades.reduce(
        (sum, guest) => sum + (guest.upgrade.selected ? guest.upgrade.upgradePrice : 0),
        0
    );
    const totalPrice = basePassPrice + upgradePrice;

    return (
        <DayPassContext.Provider
            value={{
                numberOfGuests,
                setNumberOfGuests,
                incrementGuests,
                decrementGuests,
                guestMassageUpgrades,
                setGuestMassageUpgrade,
                clearGuestMassageUpgrade,
                clearAllMassageUpgrades,
                getGuestMassageUpgrade,
                formData,
                setFormData,
                resetFormData,
                basePassPrice,
                upgradePrice,
                totalPrice,
                isTrayOpen: isTrayOpen,
                traySource,
                setIsTrayOpen,
                resetAll,
            }}
        >
            {children}
        </DayPassContext.Provider>
    );
};

export const useDayPass = () => {
    const context = useContext(DayPassContext);
    if (context === undefined) {
        throw new Error("useDayPass must be used within a DayPassProvider");
    }
    return context;
};

